(() => {
  if (!document.body.classList.contains('template-product')) return;

  const API_URL = 'https://review.wsy400.com/apps/goods-comment/list';
  const PAGE_SIZE = 100;
  const REVIEW_PAGE_SIZE = 5;
  const MAX_PAGES = 12;
  const PREVIEW_LIMIT = 10;
  const imageProtocol = /^(https?:)?\/\//i;

  if (!window.tyrotReviewPageSizeReady && typeof window.fetch === 'function') {
    const originalFetch = window.fetch.bind(window);
    window.tyrotReviewPageSizeReady = true;
    window.fetch = (input, options) => {
      const inputIsRequest = typeof Request !== 'undefined' && input instanceof Request;
      const requestUrl = inputIsRequest ? input.url : String(input);

      try {
        const url = new URL(requestUrl, window.location.href);
        if (
          url.hostname === 'review.wsy400.com' &&
          url.pathname === '/apps/goods-comment/list' &&
          url.searchParams.get('pageSize') !== String(PAGE_SIZE)
        ) {
          url.searchParams.set('pageSize', String(REVIEW_PAGE_SIZE));
          input = inputIsRequest ? new Request(url.toString(), input) : url.toString();
        }
      } catch (_error) {
        return originalFetch(input, options);
      }

      return originalFetch(input, options);
    };
  }

  const safeImageUrl = (value) => {
    if (typeof value !== 'string' || !imageProtocol.test(value)) return '';
    try {
      const url = new URL(value, window.location.href);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
    } catch (_error) {
      return '';
    }
  };

  const parseMedia = (value) => {
    if (!value) return {};
    if (typeof value === 'object') return value;
    try {
      return JSON.parse(value);
    } catch (_error) {
      return {};
    }
  };

  const reviewPhotos = (reviews) => {
    const seen = new Set();
    return reviews.flatMap((review) => {
      const media = parseMedia(review.additional_url);
      const full = Array.isArray(media.picUrls) ? media.picUrls : [];
      const small = Array.isArray(media.picUrlsSmall) ? media.picUrlsSmall : [];
      const count = Math.max(full.length, small.length);
      const photos = [];

      for (let index = 0; index < count; index += 1) {
        const src = safeImageUrl(full[index] || small[index]);
        const thumb = safeImageUrl(small[index] || full[index]);
        if (!src || seen.has(src)) continue;
        seen.add(src);
        photos.push({ src, thumb: thumb || src, review });
      }

      return photos;
    });
  };

  const fetchReviewPage = async (productId, page) => {
    const url = new URL(API_URL);
    url.searchParams.set('goodsId', productId);
    url.searchParams.set('sortType', '1');
    url.searchParams.set('star', '');
    url.searchParams.set('pageSize', String(PAGE_SIZE));
    url.searchParams.set('currentPage', String(page));
    const response = await fetch(url.toString(), { mode: 'cors', credentials: 'omit' });
    if (!response.ok) throw new Error('Review request failed');
    const payload = await response.json();
    if (String(payload.code) !== '200') throw new Error('Review response failed');
    return payload.data || {};
  };

  const formatReviewDate = (value) => {
    if (!value) return '';
    const date = new Date(Number(value));
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  const createElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (typeof text === 'string') element.textContent = text;
    return element;
  };

  const createDialog = (dialogId) => {
    const dialog = createElement('div', 'tyrot-review-dialog');
    dialog.hidden = true;
    dialog.setAttribute('aria-hidden', 'true');
    dialog.innerHTML = `
      <div class="tyrot-review-dialog__backdrop" data-tyrot-dialog-close></div>
      <div class="tyrot-review-dialog__panel" role="dialog" aria-modal="true" aria-labelledby="${dialogId}">
        <button class="tyrot-review-dialog__close" type="button" aria-label="Close customer photos" data-tyrot-dialog-close>×</button>
        <div class="tyrot-review-dialog__media">
          <div class="tyrot-review-dialog__stage">
            <button class="tyrot-review-dialog__arrow tyrot-review-dialog__arrow--previous" type="button" aria-label="Previous photo" data-tyrot-dialog-previous>‹</button>
            <img class="tyrot-review-dialog__image" alt="">
            <button class="tyrot-review-dialog__arrow tyrot-review-dialog__arrow--next" type="button" aria-label="Next photo" data-tyrot-dialog-next>›</button>
            <span class="tyrot-review-dialog__count" aria-live="polite"></span>
          </div>
          <div class="tyrot-review-dialog__thumbs" role="group" aria-label="Customer photo thumbnails"></div>
        </div>
        <article class="tyrot-review-dialog__review">
          <h3 class="tyrot-review-dialog__heading" id="${dialogId}">Customer review</h3>
          <div class="tyrot-review-dialog__author">
            <div class="tyrot-review-dialog__avatar" aria-hidden="true"></div>
            <div>
              <strong class="tyrot-review-dialog__name"></strong>
              <span class="tyrot-review-dialog__meta"></span>
            </div>
          </div>
          <div class="tyrot-review-dialog__stars"></div>
          <h4 class="tyrot-review-dialog__title"></h4>
          <p class="tyrot-review-dialog__content"></p>
          <div class="tyrot-review-dialog__reply" hidden></div>
        </article>
      </div>
    `;
    document.body.appendChild(dialog);
    return dialog;
  };

  const setupGallery = (root) => {
    if (root.dataset.tyrotReviewGallery === 'true') return;
    const productId = root.getAttribute('data-product-id');
    if (!productId) return;
    root.dataset.tyrotReviewGallery = 'true';

    const gallery = createElement('section', 'tyrot-review-gallery');
    const galleryId = `tyrot-review-gallery-${productId}`;
    gallery.id = galleryId;
    gallery.hidden = true;
    gallery.setAttribute('aria-labelledby', `${galleryId}-title`);
    gallery.innerHTML = `
      <div class="tyrot-review-gallery__header">
        <div>
          <span class="tyrot-review-gallery__eyebrow">From real customer reviews</span>
          <h3 class="tyrot-review-gallery__title" id="${galleryId}-title">Reviews with images</h3>
        </div>
        <button class="tyrot-review-gallery__view-all" type="button"></button>
      </div>
      <div class="tyrot-review-gallery__rail" role="group" aria-label="Customer review photos"></div>
    `;
    root.before(gallery);

    const rail = gallery.querySelector('.tyrot-review-gallery__rail');
    const viewAll = gallery.querySelector('.tyrot-review-gallery__view-all');
    const dialog = createDialog(`${galleryId}-dialog-title`);
    const mainImage = dialog.querySelector('.tyrot-review-dialog__image');
    const count = dialog.querySelector('.tyrot-review-dialog__count');
    const thumbs = dialog.querySelector('.tyrot-review-dialog__thumbs');
    const avatar = dialog.querySelector('.tyrot-review-dialog__avatar');
    const name = dialog.querySelector('.tyrot-review-dialog__name');
    const meta = dialog.querySelector('.tyrot-review-dialog__meta');
    const stars = dialog.querySelector('.tyrot-review-dialog__stars');
    const title = dialog.querySelector('.tyrot-review-dialog__title');
    const content = dialog.querySelector('.tyrot-review-dialog__content');
    const reply = dialog.querySelector('.tyrot-review-dialog__reply');
    let photos = [];
    let activeIndex = 0;
    let returnFocus = null;
    let priorOverflow = '';

    const showPhoto = (index) => {
      if (!photos.length) return;
      activeIndex = (index + photos.length) % photos.length;
      const photo = photos[activeIndex];
      const review = photo.review || {};
      const reviewer = review.nick || 'Customer';
      const area = review.area || '';
      const date = formatReviewDate(review.create_time);
      const starValue = Math.max(0, Math.min(5, Number(review.comment_star) || 0));
      const avatarUrl = safeImageUrl(review.nick_pic);

      mainImage.src = photo.src;
      mainImage.alt = `Review photo ${activeIndex + 1} of ${photos.length} from ${reviewer}`;
      count.textContent = `${activeIndex + 1} / ${photos.length}`;
      name.textContent = reviewer;
      meta.textContent = [area, date].filter(Boolean).join(' · ');
      stars.textContent = `${'★'.repeat(starValue)}${'☆'.repeat(5 - starValue)}`;
      stars.setAttribute('aria-label', `${starValue} out of 5 stars`);
      title.textContent = review.comment_title || '';
      title.hidden = !review.comment_title;
      content.textContent = review.comment_content || 'Customer photo';
      reply.textContent = review.replay_content ? `Tyrot Home: ${review.replay_content}` : '';
      reply.hidden = !review.replay_content;
      avatar.replaceChildren();
      if (avatarUrl) {
        const avatarImage = document.createElement('img');
        avatarImage.src = avatarUrl;
        avatarImage.alt = '';
        avatar.appendChild(avatarImage);
      } else {
        avatar.textContent = reviewer.trim().charAt(0).toUpperCase() || 'C';
      }

      thumbs.querySelectorAll('button').forEach((button, buttonIndex) => {
        button.classList.toggle('is-active', buttonIndex === activeIndex);
        if (buttonIndex === activeIndex) {
          button.setAttribute('aria-current', 'true');
          button.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        } else {
          button.removeAttribute('aria-current');
        }
      });
    };

    const renderDialogThumbs = () => {
      thumbs.replaceChildren();
      photos.forEach((photo, index) => {
        const button = createElement('button', 'tyrot-review-dialog__thumb');
        button.type = 'button';
        button.setAttribute('aria-label', `Show photo ${index + 1}`);
        const image = document.createElement('img');
        image.src = photo.thumb;
        image.alt = '';
        image.loading = 'lazy';
        button.appendChild(image);
        button.addEventListener('click', () => showPhoto(index));
        thumbs.appendChild(button);
      });
    };

    const closeDialog = () => {
      if (dialog.hidden) return;
      dialog.hidden = true;
      dialog.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('tyrot-review-dialog-open');
      document.body.style.overflow = priorOverflow;
      if (returnFocus) returnFocus.focus({ preventScroll: true });
    };

    const openDialog = (index, trigger) => {
      if (!photos.length) return;
      returnFocus = trigger || document.activeElement;
      priorOverflow = document.body.style.overflow;
      renderDialogThumbs();
      showPhoto(index);
      dialog.hidden = false;
      dialog.setAttribute('aria-hidden', 'false');
      document.body.classList.add('tyrot-review-dialog-open');
      document.body.style.overflow = 'hidden';
      dialog.querySelector('.tyrot-review-dialog__close').focus();
    };

    const renderGallery = () => {
      if (!photos.length) {
        gallery.hidden = true;
        return;
      }
      gallery.hidden = false;
      rail.replaceChildren();
      photos.slice(0, PREVIEW_LIMIT).forEach((photo, index) => {
        const reviewer = (photo.review && photo.review.nick) || 'a customer';
        const button = createElement('button', 'tyrot-review-gallery__item');
        button.type = 'button';
        button.setAttribute('aria-label', `Open review photo ${index + 1} of ${photos.length}`);
        const image = document.createElement('img');
        image.src = photo.thumb;
        image.alt = `Customer review photo from ${reviewer}`;
        image.loading = 'lazy';
        image.decoding = 'async';
        image.width = 160;
        image.height = 120;
        button.appendChild(image);
        button.addEventListener('click', () => openDialog(index, button));
        rail.appendChild(button);
      });
      viewAll.textContent = photos.length === 1 ? 'View photo' : `View all ${photos.length} photos`;
    };

    const reviewRecords = new Map();
    const labels = document.querySelector('[data-tyrot-summary-labels]')?.dataset;
    const summary = createElement('section','tyrot-customers-say');
    summary.hidden = true;
    summary.setAttribute('aria-label',labels?.title || 'Customers Say');
    gallery.before(summary);
    const topics = [
      ['color',/\b(colou?rs?|patterns?|designs?|vibrant|beautiful)\b/i],
      ['comfort',/\b(soft|softness|comfortable|comfort|cushion(?:ed|ing)?)\b/i],
      ['care',/\b(wash(?:ed|ing|able)?|clean(?:ed|ing)?|stains?)\b/i],
      ['quality',/\b(quality|durable|durability|lasting|fray(?:ed|ing)?)\b/i],
      ['fit',/\b(sizes?|fits?|fitting|length|dimensions)\b/i],
      ['light',/\b(blackout|darkening|blocks? (?:out )?light|light blocking)\b/i],
      ['grip',/\b(slips?|slipping|non.?slip|grip|slides?|sliding)\b/i]
    ];
    const renderSummary = (incoming) => {
      if (!labels) return;
      incoming.forEach(review => {
        const content = String(review.comment_content || '').replace(/<[^>]*>/g,'').trim();
        if (content.length < 12) return;
        const title = String(review.comment_title || '').replace(/<[^>]*>/g,'').trim();
        const key = `${review.nick || ''}|${content}`;
        reviewRecords.set(key,{content,title});
      });
      const records = [...reviewRecords.values()];
      if (records.length < 5) return;
      const ranked = topics.map(([key,pattern])=>({key,records:records.filter(r=>pattern.test(`${r.title} ${r.content}`))})).filter(t=>t.records.length>=2).sort((a,b)=>b.records.length-a.records.length).slice(0,3);
      if (!ranked.length) return;
      const positiveColor = /\b(beautiful|vibrant|gorgeous|stunning|lovely)\b/i;
      const colorWords = /\b(colou?rs?|patterns?|designs?)\b/i;
      const negative = /\b(not|never|isn't|aren't|wasn't|weren't|disappoint|faded|fades|but|however)\b/i;
      const sentences = record => record.content.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [record.content];
      const positiveRecords = records.filter(r => sentences(r).some(s => colorWords.test(s) && positiveColor.test(s) && !negative.test(s)));
      const topicNames = ranked.map(t => labels[t.key].toLowerCase().replace(/ & /g,' and '));
      const joinedTopics = topicNames.length > 1 ? topicNames.slice(0,-1).join(', ') + ' and ' + topicNames.at(-1) : topicNames[0];
      const positiveSummary = positiveRecords.length >= 3 && positiveRecords.length / records.length >= .15;
      const sentence = positiveSummary
        ? 'Reviewers often describe the colors and design as beautiful or vibrant.'
        : 'Customers most often comment on ' + joinedTopics + ' when describing their experience with this product.';
      const pool = positiveSummary ? positiveRecords : ranked.flatMap(t => t.records);
      const evidence = [];
      const seen = new Set();
      for (const record of pool) {
        if (seen.has(record.content)) continue;
        const excerpt = sentences(record).find(s => positiveSummary ? colorWords.test(s) && positiveColor.test(s) && !negative.test(s) : topics.some(([,pattern]) => pattern.test(s)))?.trim() || record.content;
        seen.add(record.content);
        evidence.push(record.content);
        if (evidence.length === 3) break;
      }
      if (evidence.length < 3) return;
      const wasOpen = summary.querySelector('details')?.open || false;
      const details = createElement('details','tyrot-review-evidence');
      details.open = wasOpen;
      details.append(createElement('summary','',labels.evidence));
      evidence.forEach(excerpt => details.append(createElement('blockquote','',excerpt.length>320 ? excerpt.slice(0,317).replace(/\s+\S*$/,'')+'…' : excerpt)));
      summary.replaceChildren();
      summary.append(createElement('h3','',labels.title),createElement('p','tyrot-review-summary',sentence),details);
      summary.hidden=false;
    };

    const addReviews = (reviews) => {
      renderSummary(reviews);
      const existing = new Set(photos.map((photo) => photo.src));
      reviewPhotos(reviews).forEach((photo) => {
        if (!existing.has(photo.src)) {
          existing.add(photo.src);
          photos.push(photo);
        }
      });
      renderGallery();
      if (!dialog.hidden) {
        renderDialogThumbs();
        showPhoto(activeIndex);
      }
    };

    const loadDomFallback = () => {
      const sync = () => {
        const reviews = [];
        root.querySelectorAll('[class*="CommonWidget_media"] img[class*="CommonWidget_"]').forEach((image) => {
          if (image.closest('[class*="videoItem"]')) return;
          const src = safeImageUrl(image.currentSrc || image.src);
          if (!src) return;
          const item = image.closest('[class*="ListLayout_standard"]');
          reviews.push({
            nick: item && item.querySelector('[class*="ListLayout_nick"]') ? item.querySelector('[class*="ListLayout_nick"]').textContent.trim() : 'Customer',
            comment_title: item && item.querySelector('[class*="CommonWidget_reviewTitle"]') ? item.querySelector('[class*="CommonWidget_reviewTitle"]').textContent.trim() : '',
            comment_content: item && item.querySelector('[class*="CommonWidget_reviewContent"]') ? item.querySelector('[class*="CommonWidget_reviewContent"]').textContent.trim() : '',
            additional_url: { picUrls: [src], picUrlsSmall: [src] }
          });
        });
        addReviews(reviews);
      };
      sync();
      const observer = new MutationObserver(sync);
      observer.observe(root, { childList: true, subtree: true });
      window.setTimeout(() => observer.disconnect(), 15000);
    };

    viewAll.addEventListener('click', () => openDialog(0, viewAll));
    dialog.querySelectorAll('[data-tyrot-dialog-close]').forEach((button) => button.addEventListener('click', closeDialog));
    dialog.querySelector('[data-tyrot-dialog-previous]').addEventListener('click', () => showPhoto(activeIndex - 1));
    dialog.querySelector('[data-tyrot-dialog-next]').addEventListener('click', () => showPhoto(activeIndex + 1));
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeDialog();
      if (event.key === 'ArrowLeft') showPhoto(activeIndex - 1);
      if (event.key === 'ArrowRight') showPhoto(activeIndex + 1);
      if (event.key !== 'Tab') return;
      const focusable = [...dialog.querySelectorAll('button:not([disabled])')];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const loadReviews = () => fetchReviewPage(productId, 1).then(async (firstPage) => {
      addReviews(Array.isArray(firstPage.dataList) ? firstPage.dataList : []);
      const totalPages = Math.min(Number(firstPage.totalPages) || 1, MAX_PAGES);
      if (totalPages <= 1) return;
      const remainingPages = Array.from({ length: totalPages - 1 }, (_value, index) => index + 2);
      const remaining = await Promise.allSettled(remainingPages.map((page) => fetchReviewPage(productId, page)));
      remaining.forEach((result) => {
        if (result.status === 'fulfilled') addReviews(Array.isArray(result.value.dataList) ? result.value.dataList : []);
      });
    }).catch(loadDomFallback);
    if ('IntersectionObserver' in window) {
      const reviewObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        reviewObserver.disconnect();
        loadReviews();
      }, {rootMargin:'800px 0px'});
      reviewObserver.observe(root);
    } else loadReviews();
  };

  const start = (scope = document) => {
    if (scope.matches?.('.root_comment_div[data-product-id]')) setupGallery(scope);
    scope.querySelectorAll?.('.root_comment_div[data-product-id]').forEach(setupGallery);
  };

  start();
  new MutationObserver(mutations => {
    mutations.forEach(mutation => mutation.addedNodes.forEach(node => {
      if (node.nodeType === 1) start(node);
    }));
  }).observe(document.body, { childList: true, subtree: true });
})();

/* Compact, user-controllable homepage trust carousel. */
(() => {
 function initTrust(root=document) {
  root.querySelectorAll('.tyrot-v2__trust-grid').forEach(grid=>{
   if(grid.dataset.trustReady)return;
   grid.dataset.trustReady='true';
   const items=Array.from(grid.children); if(items.length<2)return;
   let paused=false;
   const controls=document.createElement('div'); controls.className='tyrot-v2__trust-controls';
   const prev=document.createElement('button'), next=document.createElement('button'), count=document.createElement('span');
   prev.type=next.type='button'; prev.textContent='‹'; next.textContent='›';
   prev.setAttribute('aria-label','Previous benefit'); next.setAttribute('aria-label','Next benefit');
   count.textContent='1 / '+items.length;
   controls.append(prev,count,next); grid.after(controls);
   grid.setAttribute('aria-label','Store benefits'); grid.setAttribute('role','region');
   const mobile=()=>matchMedia('(max-width:767px)').matches;
   const position=()=>Math.round(grid.scrollLeft/Math.max(1,grid.clientWidth));
   function move(step){if(!mobile())return;const index=(position()+step+items.length)%items.length;grid.scrollTo({left:index*grid.clientWidth,behavior:matchMedia('(prefers-reduced-motion:reduce)').matches?'auto':'smooth'});}
   prev.onclick=()=>{paused=true;move(-1)}; next.onclick=()=>{paused=true;move(1)};
   grid.addEventListener('pointerdown',()=>{paused=true},{passive:true});
   grid.addEventListener('focusin',()=>{paused=true});
   grid.addEventListener('scroll',()=>{count.textContent=(position()+1)+' / '+items.length},{passive:true});
   const timer=setInterval(()=>{
    if(!grid.isConnected){clearInterval(timer);return;}
    if(paused||document.hidden||!mobile()||matchMedia('(prefers-reduced-motion:reduce)').matches)return;
    const r=grid.getBoundingClientRect();if(r.top<innerHeight&&r.bottom>0)move(1);
   },6000);
  });
 }
 if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>initTrust());else initTrust();
 document.addEventListener('shopify:section:load',e=>initTrust(e.target));
})();



